/**
 * Overlay stack (drawers, dialogs, the command palette, row menus). Esc closes only the top-most
 * layer, focus is trapped inside it (a menu closes on Tab instead), and it is restored to the
 * opener on close. Global shortcuts ask `isBlockingLayerOpen()` / `isAnyLayerOpen()` before acting.
 */
import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

export type LayerKind = 'drawer' | 'dialog' | 'palette' | 'menu';

interface Layer {
  readonly id: number;
  readonly kind: LayerKind;
}

let nextLayerId = 1;
const stack: Layer[] = [];

export function isTopLayer(id: number): boolean {
  return stack[stack.length - 1]?.id === id;
}

/** True while a dialog, menu or the palette is open (drawers do not block page shortcuts). */
export function isBlockingLayerOpen(): boolean {
  return stack.some((layer) => layer.kind !== 'drawer');
}

/** True while any overlay is open, drawers included (focus must not leave a trapped drawer). */
export function isAnyLayerOpen(): boolean {
  return stack.length > 0;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true',
  );
}

export interface UseLayerOptions {
  readonly kind: LayerKind;
  readonly panelRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
  /** Element focused on open; default: the first focusable element, else the panel. */
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
  /** `first` focuses the first focusable element; `panel` focuses the panel itself. */
  readonly initialFocus?: 'first' | 'panel';
}

function trapTab(event: KeyboardEvent, panel: HTMLElement): void {
  const items = focusableWithin(panel);
  if (items.length === 0) {
    event.preventDefault();
    panel.focus();
    return;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement;
  const inside = active instanceof Node && panel.contains(active);
  if (event.shiftKey && (active === first || active === panel || !inside)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || !inside)) {
    event.preventDefault();
    first.focus();
  }
}

/** Registers an open overlay: Esc to close, Tab trapped, focus restored on unmount. */
export function useLayer({
  kind,
  panelRef,
  onClose,
  initialFocusRef,
  initialFocus = 'first',
}: UseLayerOptions): void {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const layer: Layer = { id: nextLayerId++, kind };
    stack.push(layer);
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const panel = panelRef.current;
    const target =
      initialFocusRef?.current ??
      (initialFocus === 'first' && panel ? focusableWithin(panel)[0] : undefined) ??
      panel;
    target?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTopLayer(layer.id)) return;
      const closesOnTab = kind === 'menu' && event.key === 'Tab';
      if (event.key === 'Escape' || closesOnTab) {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
      } else if (event.key === 'Tab' && panelRef.current) {
        trapTab(event, panelRef.current);
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const index = stack.findIndex((l) => l.id === layer.id);
      if (index >= 0) stack.splice(index, 1);
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    };
    // Runs once per open: the overlay mounts when opened and unmounts when closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
